import { useEffect, useState } from 'react'
import { bettingApi } from '../../api'
import { activeLang, enumLabel, formattersFor, useFormat, useT } from '../../i18n'
import type { TFunction } from '../../i18n'
import type {
  AggregatorStatus,
  BettingVocabulary,
  BreachType,
  ComplianceSeverity,
  JourneyDepth,
  PaymentTestRunStatus,
  PaymentTestSource,
} from '../../types'

/** Ordre d'affichage : du plus grave au conforme. */
export const SEVERITY_ORDER: ComplianceSeverity[] = ['HIGH', 'MEDIUM', 'LOW', 'TECHNICAL_ISSUE', 'COMPLIANT']

export const BREACH_TYPES: BreachType[] = [
  'UNAUTHORISED_AGGREGATOR',
  'PROHIBITED_SERVICE',
  'UNLICENSED_OPERATOR',
  'UNAUTHORISED_BRAND_USE',
  'NON_COMPLIANT_ADVERTISING',
  'TECHNICAL_ISSUE',
  'OTHER',
]

export const AGGREGATOR_STATUSES: AggregatorStatus[] = ['AUTHORIZED', 'UNAUTHORIZED', 'UNKNOWN']

/**
 * Libellés des valeurs du module, traduits à l'affichage.
 *
 * Les codes restent ceux du serveur et des journaux : eux ne changent jamais
 * de langue.
 */
export const severityLabel = (t: TFunction, s: ComplianceSeverity | null | undefined) =>
  s ? enumLabel(t, 'bet.sev', s, s) : t('bet.sev.UNQUALIFIED')
export const breachLabel = (t: TFunction, b: BreachType | null | undefined) =>
  enumLabel(t, 'bet.breach', b, b ?? '—')
export const runStatusLabel = (t: TFunction, s: PaymentTestRunStatus) => enumLabel(t, 'bet.run', s, s)
export const sourceLabel = (t: TFunction, s: PaymentTestSource) => enumLabel(t, 'bet.source', s, s)
export const depthLabel = (t: TFunction, d: JourneyDepth | null | undefined) =>
  enumLabel(t, 'bet.depth', d, d ?? '—')
export const aggregatorStatusLabel = (t: TFunction, s: AggregatorStatus | null | undefined) =>
  enumLabel(t, 'bet.agg', s ?? 'UNKNOWN', s ?? '—')

export function SeverityBadge({ severity }: { severity: ComplianceSeverity | null }) {
  const t = useT()
  const key = severity ?? 'UNQUALIFIED'
  return (
    <span className={`bet-severity bet-severity-${key.toLowerCase()}`}>
      <span className="bet-severity-dot" aria-hidden="true" />
      {severityLabel(t, severity)}
    </span>
  )
}

export function RunStatusBadge({ status }: { status: PaymentTestRunStatus }) {
  const t = useT()
  return <span className={`bet-run bet-run-${status.toLowerCase()}`}>{runStatusLabel(t, status)}</span>
}

export function AggregatorStatusPill({ status }: { status: AggregatorStatus | null }) {
  const t = useT()
  const s = status ?? 'UNKNOWN'
  return <span className={`bet-agg-status bet-agg-${s.toLowerCase()}`}>{aggregatorStatusLabel(t, s)}</span>
}

/** Vocabulaire partagé (pays couverts, gravités, infractions), chargé une fois. */
let vocabularyCache: Promise<BettingVocabulary> | null = null
export function useBettingVocabulary(): BettingVocabulary | null {
  const [vocab, setVocab] = useState<BettingVocabulary | null>(null)
  useEffect(() => {
    vocabularyCache ??= bettingApi.vocabulary().catch((err) => {
      vocabularyCache = null
      throw err
    })
    let alive = true
    vocabularyCache.then((v) => alive && setVocab(v)).catch(() => undefined)
    return () => {
      alive = false
    }
  }, [])
  return vocab
}

/**
 * Nom d'un pays du module.
 *
 * Le serveur en envoie une version française dans son vocabulaire : le
 * catalogue prend le dessus quand il connaît le code, et le libellé du serveur
 * ne sert plus que de dernier recours.
 */
export function countryLabel(vocab: BettingVocabulary | null, code: string, t?: TFunction): string {
  const fromServer = vocab?.countries.find((c) => c.code === code)?.label ?? code
  return t ? enumLabel(t, 'country', code, fromServer) : fromServer
}

/**
 * Image de preuve chargée avec le jeton : les preuves ne sont pas servies par
 * simple lien, l'URL locale est libérée quand l'image disparaît.
 */
export function EvidenceImage({
  testId,
  evidenceKey,
  alt,
  className,
  onClick,
}: {
  testId: string
  evidenceKey: string | null
  alt: string
  className?: string
  onClick?: () => void
}) {
  const t = useT()
  const [url, setUrl] = useState<string | null>(null)
  const [failed, setFailed] = useState(false)

  useEffect(() => {
    if (!evidenceKey) return
    let revoked = false
    let objectUrl: string | null = null
    bettingApi
      .evidenceObjectUrl(testId, evidenceKey)
      .then((u) => {
        objectUrl = u
        if (revoked) URL.revokeObjectURL(u)
        else setUrl(u)
      })
      .catch(() => setFailed(true))
    return () => {
      revoked = true
      if (objectUrl) URL.revokeObjectURL(objectUrl)
    }
  }, [testId, evidenceKey])

  if (!evidenceKey || failed) {
    return (
      <div className={`bet-evidence-missing ${className ?? ''}`}>
        {evidenceKey ? t('bet.evidenceMissing') : t('bet.evidenceNone')}
      </div>
    )
  }
  if (!url) return <div className={`bet-evidence-loading ${className ?? ''}`} aria-label={t('bet.evidenceLoading')} />
  if (onClick) {
    return (
      <button type="button" className={`bet-evidence-button ${className ?? ''}`} onClick={onClick} aria-label={t('bet.evidenceZoom', { alt })}>
        <img src={url} alt={alt} />
      </button>
    )
  }
  return <img src={url} alt={alt} className={className} />
}

/**
 * Date et heure d'un test.
 *
 * La langue est lue hors rendu : la fonction est appelée dans des cellules de
 * tableau sans contexte à portée. `useDateTime` en donne la version réactive,
 * à préférer dans un composant.
 */
export function formatDateTime(iso: string | null): string {
  return formattersFor(activeLang()).dateTime(iso)
}

/** Version réactive : à préférer dans un composant. */
export function useDateTime(): (iso: string | null | undefined) => string {
  const fmt = useFormat()
  return (iso) => fmt.dateTime(iso)
}

/**
 * Interrupteur des données de démonstration.
 *
 * Il n'apparaît que si le serveur autorise les sites factices. Sans lui, les
 * services et tests fictifs restent invisibles et hors des chiffres.
 */
export function FixtureToggle({ checked, onChange }: { checked: boolean; onChange: (v: boolean) => void }) {
  const t = useT()
  const vocab = useBettingVocabulary()
  if (!vocab?.fixturesEnabled) return null
  return (
    <label className="bet-fixture-toggle" title={t('bet.fixtureToggleHint')}>
      <input type="checkbox" checked={checked} onChange={(e) => onChange(e.target.checked)} />
      <span>{t('bet.fixtureToggle')}</span>
    </label>
  )
}

/** Marque une ligne ou une fiche issue d'un site factice. */
export function FixtureBadge({ when }: { when: boolean | undefined }) {
  const t = useT()
  if (!when) return null
  return <span className="bet-fixture-badge" title={t('bet.fixtureBadgeHint')}>{t('bet.fixtureBadge')}</span>
}
